/**
 * Profile Page Component
 *
 * User profile management page with information editing and security settings
 *
 * @module pages/Profile
 */

import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, Modal, ProgressBar } from 'react-bootstrap';
import { toast } from '../utils/toast';
import { useAuth } from '../contexts/AuthContext';
import { authService, supabase } from '../services/supabase';
import { formatDate } from '../utils/formatters';
import DashboardLayout from '../components/layout/DashboardLayout';
import BackgroundSelector from '../components/dashboard/BackgroundSelector';

const Profile = () => {
  const { user, profile, updateProfile, signOut } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loadTimeout, setLoadTimeout] = useState(false);

  // If profile hasn't arrived after 10 s, show a connection-error instead of a
  // spinner that never resolves (happens when Supabase is offline or paused).
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!profile) setLoadTimeout(true);
    }, 10000);
    return () => clearTimeout(timer);
  }, [profile]);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState({
    full_name: profile?.full_name || '',
    phone: profile?.phone || '',
  });

  // Update form when profile loads
  React.useEffect(() => {
    if (profile) {
      setFormData({
        full_name: profile.full_name || '',
        phone: profile.phone || '',
      });
    }
  }, [profile]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.full_name.trim()) {
      toast.error('El nombre completo es requerido');
      return;
    }

    setIsSaving(true);

    try {
      const { success, error } = await updateProfile(formData);

      if (success) {
        toast.success('Perfil actualizado correctamente');
        setIsEditing(false);
      } else {
        throw error;
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.message || 'Error al actualizar el perfil');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset form to current profile data
    setFormData({
      full_name: profile?.full_name || '',
      phone: profile?.phone || '',
    });
    setIsEditing(false);
  };

  // Avatar upload state
  const fileInputRef = useRef(null);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || null);

  React.useEffect(() => {
    if (profile?.avatar_url) setAvatarUrl(profile.avatar_url);
  }, [profile?.avatar_url]);

  const getInitials = useCallback(() => {
    const name = profile?.full_name || '';
    return name.split(' ').map(n => n[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  }, [profile?.full_name]);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Solo se permiten imágenes JPG, PNG o WebP');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen no debe superar 2MB');
      return;
    }

    setIsUploadingAvatar(true);
    try {
      const ext = file.name.split('.').pop();
      const filePath = `${user.id}/avatar.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { cacheControl: '3600', upsert: true });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;

      const { success, error } = await updateProfile({ avatar_url: publicUrl });
      if (!success) throw error;

      setAvatarUrl(publicUrl);
      toast.success('Foto de perfil actualizada');
    } catch (err) {
      console.error('Avatar upload error:', err);
      toast.error('Error al subir la imagen');
    } finally {
      setIsUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwords, setPasswords] = useState({ newPassword: '', confirmPassword: '' });
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  const passwordStrength = useMemo(() => {
    const pw = passwords.newPassword;
    if (!pw) return { score: 0, label: '', variant: '' };
    let score = 0;
    if (pw.length >= 8) score++;
    if (/[A-Z]/.test(pw)) score++;
    if (/[a-z]/.test(pw)) score++;
    if (/[0-9]/.test(pw)) score++;
    if (score <= 1) return { score: 25, label: 'Debil', variant: 'danger' };
    if (score === 2) return { score: 50, label: 'Regular', variant: 'warning' };
    if (score === 3) return { score: 75, label: 'Buena', variant: 'info' };
    return { score: 100, label: 'Fuerte', variant: 'success' };
  }, [passwords.newPassword]);

  const passwordErrors = useMemo(() => {
    const pw = passwords.newPassword;
    const errors = [];
    if (pw && pw.length < 8) errors.push('Minimo 8 caracteres');
    if (pw && !/[A-Z]/.test(pw)) errors.push('Una letra mayuscula');
    if (pw && !/[a-z]/.test(pw)) errors.push('Una letra minuscula');
    if (pw && !/[0-9]/.test(pw)) errors.push('Un numero');
    return errors;
  }, [passwords.newPassword]);

  const handleChangePassword = async () => {
    if (passwords.newPassword !== passwords.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    if (passwordErrors.length > 0) {
      toast.error('La contraseña no cumple los requisitos');
      return;
    }
    setIsChangingPassword(true);
    try {
      const { success, error } = await authService.updatePassword(passwords.newPassword);
      if (success) {
        toast.success('Contraseña actualizada correctamente');
        setShowPasswordModal(false);
        setPasswords({ newPassword: '', confirmPassword: '' });
      } else {
        throw error;
      }
    } catch (err) {
      toast.error(err?.message || 'Error al cambiar la contraseña');
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleSignOutAll = async () => {
    try {
      // `global` EXPLÍCITO: este botón promete cerrar todas las sesiones, y es
      // el único lugar de la app que lo promete. El logout normal (el del menú,
      // el del portal del técnico) es `local` desde v0.66.0 — cerrar sesión en
      // el celular no tiene por qué sacarte de la computadora.
      await signOut({ scope: 'global' });
      toast.success('Sesión cerrada correctamente');
    } catch (err) {
      toast.error('Error al cerrar la sesión');
    }
    setShowSignOutConfirm(false);
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'admin':
        return 'danger';
      case 'supervisor':
        return 'warning';
      case 'technician':
        return 'primary';
      case 'client':
        return 'info';
      default:
        return 'secondary';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'supervisor':
        return 'Supervisor';
      case 'technician':
        return 'Técnico / Líder';
      case 'client':
        return 'Cliente';
      default:
        return role;
    }
  };

  if (!profile) {
    // After 10 s with no profile, assume the server is unreachable
    if (loadTimeout) {
      return (
        <DashboardLayout>
          <Container className="py-5">
            <div className="text-center py-5">
              <i className="bi bi-exclamation-triangle fs-1 text-warning"></i>
              <h5 className="mt-3">No se pudo cargar el perfil</h5>
              <p className="text-muted">El servidor no responde. Intenta recargar la página.</p>
              <Button variant="outline-primary" size="sm" onClick={() => window.location.reload()}>
                <i className="bi bi-arrow-clockwise me-1"></i>Recargar
              </Button>
            </div>
          </Container>
        </DashboardLayout>
      );
    }

    // Still within the 10-s grace period — show spinner
    return (
      <DashboardLayout>
        <Container className="py-5">
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
          </div>
        </Container>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <h1 className="h3 mb-0">Mi Perfil</h1>
          <p className="text-muted">Gestiona tu información personal y configuración de seguridad</p>
        </Col>
      </Row>

      <Row className="g-4">
        {/* Profile Information */}
        <Col lg={8}>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">
                <i className="bi bi-person-circle me-2"></i>
                Información Personal
              </h5>
            </Card.Header>
            <Card.Body>
              {/* Avatar Section */}
              <div className="text-center mb-4">
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleAvatarUpload}
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                />
                {/* Era un <div onClick> cuyo único nombre accesible era un
                    `title`: en touch el title no se muestra nunca, y sin
                    tabIndex ni rol no había forma de llegar por teclado. Un
                    <button> real trae foco, Enter/Espacio y nombre accesible
                    de fábrica; el reset de estilo lo deja idéntico. El copy
                    tampoco puede decir "click" en un teléfono. */}
                <button
                  type="button"
                  onClick={handleAvatarClick}
                  className="hex-avatar hex-avatar-xl"
                  disabled={isUploadingAvatar}
                  style={{
                    margin: '0 auto', cursor: 'pointer', position: 'relative',
                    background: avatarUrl ? 'transparent' : '#0d6efd',
                    border: 'none', padding: 0,
                  }}
                  aria-label="Cambiar foto de perfil"
                  title="Cambiar foto de perfil"
                  data-testid="profile-avatar-button"
                >
                  {isUploadingAvatar ? (
                    <div className="spinner-border text-light" role="status">
                      <span className="visually-hidden">Subiendo...</span>
                    </div>
                  ) : avatarUrl ? (
                    <img src={avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    getInitials()
                  )}
                  <span style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)', padding: '4px 0',
                    opacity: isUploadingAvatar ? 0 : 1,
                  }}>
                    <i className="bi bi-camera text-white" style={{ fontSize: '0.9rem' }} aria-hidden="true"></i>
                  </span>
                </button>
                <small className="text-muted d-block mt-1">Tocá la foto para cambiarla</small>
              </div>

              {/* Display Mode */}
              {!isEditing && (
                <div>
                  <Row className="mb-3">
                    <Col sm={4} className="text-muted">
                      <strong>Nombre Completo:</strong>
                    </Col>
                    <Col sm={8}>
                      <span data-testid="profile-full-name-display">
                        {profile.full_name || 'No especificado'}
                      </span>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col sm={4} className="text-muted">
                      <strong>Correo Electrónico:</strong>
                    </Col>
                    <Col sm={8}>
                      <span data-testid="profile-email-display">
                        {user?.email}
                      </span>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col sm={4} className="text-muted">
                      <strong>Teléfono:</strong>
                    </Col>
                    <Col sm={8}>
                      <span data-testid="profile-phone-display">
                        {profile.phone || 'No especificado'}
                      </span>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col sm={4} className="text-muted">
                      <strong>Rol:</strong>
                    </Col>
                    <Col sm={8}>
                      <Badge bg={getRoleBadgeVariant(profile.role)}>
                        {getRoleLabel(profile.role)}
                      </Badge>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col sm={4} className="text-muted">
                      <strong>Fecha de Registro:</strong>
                    </Col>
                    <Col sm={8}>
                      {formatDate(profile.created_at)}
                    </Col>
                  </Row>

                  <div className="mt-4">
                    <Button
                      variant="primary"
                      onClick={() => setIsEditing(true)}
                    >
                      <i className="bi bi-pencil me-2"></i>
                      Editar Información
                    </Button>
                  </div>
                </div>
              )}

              {/* Edit Mode */}
              {isEditing && (
                <Form onSubmit={handleSave}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nombre Completo *</Form.Label>
                    <Form.Control
                      type="text"
                      name="full_name"
                      value={formData.full_name}
                      onChange={handleInputChange}
                      required
                      data-testid="profile-edit-name-input"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Correo Electrónico</Form.Label>
                    <Form.Control
                      type="email"
                      value={user?.email}
                      disabled
                      readOnly
                    />
                    <Form.Text className="text-muted">
                      El correo electrónico no puede ser modificado.
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Teléfono</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="+506 XXXX-XXXX"
                      data-testid="profile-edit-phone-input"
                    />
                    <Form.Text className="text-muted">
                      Formato recomendado: +506 XXXX-XXXX
                    </Form.Text>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Rol</Form.Label>
                    <div>
                      <Badge bg={getRoleBadgeVariant(profile.role)}>
                        {getRoleLabel(profile.role)}
                      </Badge>
                      <Form.Text className="d-block text-muted">
                        El rol es asignado por un administrador y no puede ser modificado.
                      </Form.Text>
                    </div>
                  </Form.Group>

                  <div className="d-flex gap-2">
                    <Button
                      type="submit"
                      variant="primary"
                      disabled={isSaving}
                      data-testid="profile-save-button"
                    >
                      {isSaving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Guardando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-check-circle me-2"></i>
                          Guardar Cambios
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      onClick={handleCancel}
                      disabled={isSaving}
                    >
                      Cancelar
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Security Settings */}
        <Col lg={4}>
          <Card>
            <Card.Header className="bg-secondary text-white">
              <h5 className="mb-0">
                <i className="bi bi-shield-lock me-2"></i>
                Seguridad
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">
                Gestiona la seguridad de tu cuenta
              </p>

              <div className="d-grid gap-2 mb-3">
                <Button
                  variant="outline-primary"
                  onClick={() => setShowPasswordModal(true)}
                >
                  <i className="bi bi-key me-2"></i>
                  Cambiar Contraseña
                </Button>
              </div>

              <hr />

              <div className="small mb-2">
                <strong className="text-muted">Último inicio de sesión:</strong>
                <br />
                {user?.last_sign_in_at
                  ? formatDate(user.last_sign_in_at)
                  : 'No disponible'}
              </div>

              <div className="d-grid mt-3">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setShowSignOutConfirm(true)}
                >
                  <i className="bi bi-box-arrow-right me-2"></i>
                  Cerrar Todas las Sesiones
                </Button>
              </div>
            </Card.Body>
          </Card>

          {/* Account Information */}
          <Card className="mt-3">
            <Card.Header>
              <h6 className="mb-0">
                <i className="bi bi-info-circle me-2"></i>
                Información de Cuenta
              </h6>
            </Card.Header>
            <Card.Body>
              <div className="small">
                <div className="mb-2">
                  <strong className="text-muted">ID de Usuario:</strong>
                  <br />
                  <code className="small">{user?.id}</code>
                </div>
                <div className="mb-2">
                  <strong className="text-muted">Última Actualización:</strong>
                  <br />
                  {formatDate(profile.updated_at || profile.created_at)}
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Appearance — dashboard background preference (issue #414) */}
      <Row className="g-4 mt-1">
        <Col xs={12}>
          <Card>
            <Card.Header className="bg-dark text-white">
              <h5 className="mb-0">
                <i className="bi bi-palette me-2"></i>
                Apariencia
              </h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small mb-3">
                Elegí cómo se ve el fondo del panel. Por defecto cambia según la sección en la que estás.
              </p>
              <BackgroundSelector />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Change Password Modal */}
      <Modal show={showPasswordModal} onHide={() => { setShowPasswordModal(false); setPasswords({ newPassword: '', confirmPassword: '' }); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-key me-2"></i>
            Cambiar Contrasena
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Nueva Contraseña</Form.Label>
            <Form.Control
              type="password"
              value={passwords.newPassword}
              onChange={(e) => setPasswords(prev => ({ ...prev, newPassword: e.target.value }))}
              placeholder="Ingresa tu nueva contraseña"
            />
          </Form.Group>

          {passwords.newPassword && (
            <div className="mb-3">
              <div className="d-flex justify-content-between mb-1">
                <small className="text-muted">Seguridad:</small>
                <small className={`text-${passwordStrength.variant}`}>{passwordStrength.label}</small>
              </div>
              <ProgressBar
                now={passwordStrength.score}
                variant={passwordStrength.variant}
                style={{ height: '6px' }}
              />
              {passwordErrors.length > 0 && (
                <div className="mt-2">
                  {passwordErrors.map((err, i) => (
                    <small key={i} className="d-block text-danger">
                      <i className="bi bi-x-circle me-1"></i>{err}
                    </small>
                  ))}
                </div>
              )}
            </div>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Confirmar Contraseña</Form.Label>
            <Form.Control
              type="password"
              value={passwords.confirmPassword}
              onChange={(e) => setPasswords(prev => ({ ...prev, confirmPassword: e.target.value }))}
              placeholder="Confirma tu nueva contraseña"
              isInvalid={passwords.confirmPassword && passwords.newPassword !== passwords.confirmPassword}
            />
            {passwords.confirmPassword && passwords.newPassword !== passwords.confirmPassword && (
              <Form.Control.Feedback type="invalid">
                Las contraseñas no coinciden
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowPasswordModal(false); setPasswords({ newPassword: '', confirmPassword: '' }); }}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            onClick={handleChangePassword}
            disabled={isChangingPassword || !passwords.newPassword || !passwords.confirmPassword || passwordErrors.length > 0 || passwords.newPassword !== passwords.confirmPassword}
          >
            {isChangingPassword ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Cambiando...
              </>
            ) : (
              'Cambiar Contrasena'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Sign Out Confirmation Modal */}
      <Modal show={showSignOutConfirm} onHide={() => setShowSignOutConfirm(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title className="h6">Cerrar Sesiones</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* El texto decía "se cerrará tu sesión actual", que es lo que hace
              el logout del menú — no lo que hace este botón. Acá sí se cierran
              todas, en todos los dispositivos, porque es lo que promete. */}
          <p className="mb-0">
            Se cerrarán tus sesiones en todos los dispositivos. Tendrás que iniciar sesión de nuevo.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowSignOutConfirm(false)}>
            Cancelar
          </Button>
          <Button variant="danger" size="sm" onClick={handleSignOutAll}>
            <i className="bi bi-box-arrow-right me-2"></i>
            Cerrar Sesión
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
    </DashboardLayout>
  );
};

export default Profile;
